import { z } from 'zod';
import { listInput } from './list';
import { idempotency, toJobFields } from './spine';
import { QUOTE_STATES } from '../statemachines/quote';

const optionalText = (max: number) =>
  z.string().trim().max(max).optional().or(z.literal(''));
/** Both instants travel together; null clears a saved header window. */
const requestedWindow = {
  requestedStartAt: z.coerce.date().nullable().optional(),
  requestedEndAt: z.coerce.date().nullable().optional(),
};
const validRequestedWindow = (v: {
  requestedStartAt?: Date | null;
  requestedEndAt?: Date | null;
}) =>
  (v.requestedStartAt === undefined && v.requestedEndAt === undefined) ||
  (v.requestedStartAt === null && v.requestedEndAt === null) ||
  (v.requestedStartAt instanceof Date &&
    v.requestedEndAt instanceof Date &&
    v.requestedStartAt < v.requestedEndAt);

// ── Line items (shared by quotes + invoices — one shape, two tables, Q1) ──────
export const lineItemKind = z.enum(['labor', 'material', 'service', 'fee', 'discount']);
export type LineItemKind = z.infer<typeof lineItemKind>;

export const lineItemInput = z.object({
  priceBookItemId: z.string().uuid().optional(),
  kind: lineItemKind,
  name: z.string().trim().min(1).max(200),
  description: optionalText(2000),
  qty: z.coerce.number().positive().max(100_000),
  unitPriceCents: z.coerce.number().int().min(-100_000_000).max(100_000_000),
  taxable: z.boolean().default(true),
});
export type LineItemInput = z.infer<typeof lineItemInput>;

/** Proposed sourcing and assignment on a quote are intent, never a stock or calendar hold. */
export const quoteLineItemInput = lineItemInput
  .extend({
    sourceLocationId: z.string().uuid().optional(),
    assignedUserId: z.string().uuid().optional(),
    taskName: z.string().trim().min(1).max(200).optional(),
    unitLabel: z.string().trim().min(1).max(20).optional(),
    estimatedDurationMinutes: z.number().int().min(1).max(525600).optional(),
    ...requestedWindow,
  })
  .strict()
  .refine(validRequestedWindow, {
    message: 'Choose both requested times, with the end after the start',
    path: ['requestedEndAt'],
  })
  .refine((v) => !v.requestedStartAt || v.kind === 'service' || v.kind === 'labor', {
    message: 'Requested crew times belong to service or labor lines',
    path: ['requestedStartAt'],
  });
export type QuoteLineItemInput = z.infer<typeof quoteLineItemInput>;

// ── Price book ────────────────────────────────────────────────────────────────
export const priceBookItemInput = idempotency.extend({
  kind: z.enum(['service', 'labor', 'material', 'fee']),
  name: z.string().trim().min(1).max(200),
  description: optionalText(2000),
  unitLabel: optionalText(20),
  unitPriceCents: z.coerce.number().int().min(0).max(100_000_000),
  taxable: z.boolean().default(true),
  category: optionalText(60),
});
export type PriceBookItemInput = z.infer<typeof priceBookItemInput>;

// ── Quotes ────────────────────────────────────────────────────────────────────
export const quoteCreateInput = idempotency
  .extend({
    // Nullable legacy storage is an upgrade bridge; active workflow services require this parent.
    commercialJobId: z.string().uuid().optional(),
    customerId: z.string().uuid(),
    propertyId: z.string().uuid().optional(),
    estimatorUserId: z.string().uuid().nullable().optional(),
    ...requestedWindow,
    title: optionalText(200),
    termsText: optionalText(4000),
    validUntil: z.coerce.date().optional(),
    /** Optional, NOT defaulted to 0. The server has to be able to tell "the user typed
     *  0%" from "the caller said nothing" — the first is a decision (resale, out of
     *  state), the second means fall back to the shop's rate. `.default(0)` collapsed
     *  both into 0 and is why every invoice in the product went out untaxed. */
    taxRateBps: z.coerce.number().int().min(0).max(20_000).optional(),
    depositRequiredCents: z.coerce.number().int().min(0).optional(),
    lines: z.array(quoteLineItemInput).min(1).max(200),
  })
  .strict()
  .refine(validRequestedWindow, {
    message: 'Choose both requested times, with the end after the start',
    path: ['requestedEndAt'],
  });
export type QuoteCreateInput = z.infer<typeof quoteCreateInput>;

export const quotePreviewInput = z
  .object({
    customerId: z.string().uuid().optional(),
    taxRateBps: z.number().int().min(0).max(20000).optional(),
    lines: z.array(quoteLineItemInput).max(200),
  })
  .strict();
export type QuotePreviewInput = z.infer<typeof quotePreviewInput>;
export const quoteWorkspaceListInput = listInput
  .extend({
    commercialJobId: z.string().uuid().optional(),
    ownerUserId: z.string().uuid().optional(),
    pipelineId: z.string().uuid().optional(),
    status: z.enum(QUOTE_STATES).optional(),
  })
  .strict();
export type QuoteWorkspaceListInput = z.infer<typeof quoteWorkspaceListInput>;
export const quoteFamilyListInput = z
  .object({
    commercialJobId: z.string().uuid(),
    ownerUserId: z.string().uuid().optional(),
    pipelineId: z.string().uuid().optional(),
    search: listInput.shape.search,
    status: z.enum(QUOTE_STATES).optional(),
    limit: z.number().int().min(1).max(50).default(20),
    offset: z.number().int().min(0).default(0),
  })
  .strict();
export type QuoteFamilyListInput = z.infer<typeof quoteFamilyListInput>;
export const quoteRevisionListInput = z
  .object({
    quoteFamilyId: z.string().uuid(),
    limit: z.number().int().min(1).max(50).default(20),
    offset: z.number().int().min(0).default(0),
  })
  .strict();
export type QuoteRevisionListInput = z.infer<typeof quoteRevisionListInput>;
export const quoteUpdateInput = idempotency
  .extend({
    quoteId: z.string().uuid(),
    expectedVersion: z.number().int().positive(),
    title: z.string().trim().max(200).optional(),
    termsText: z.string().trim().max(4000).optional(),
    validUntil: z.coerce.date().nullable().optional(),
    propertyId: z.string().uuid().nullable().optional(),
    estimatorUserId: z.string().uuid().nullable().optional(),
    ...requestedWindow,
    taxRateBps: z.number().int().min(0).max(20000).optional(),
    depositRequiredCents: z.number().int().min(0).nullable().optional(),
    lines: z.array(quoteLineItemInput).min(1).max(200).optional(),
  })
  .strict()
  .refine(validRequestedWindow, {
    message: 'Choose both requested times, with the end after the start',
    path: ['requestedEndAt'],
  });
export type QuoteUpdateInput = z.infer<typeof quoteUpdateInput>;

export const quoteIdInput = idempotency.extend({ quoteId: z.string().uuid() });
export type QuoteIdInput = z.infer<typeof quoteIdInput>;

/**
 * Email a quote to its customer. No address input (MAP 249 spec §3, ticket 02): the recipient
 * is always the customer's address on file. An `email` a caller still sends is STRIPPED here
 * (zod's default), so it can never reach the outbox: the open relay (R1 gap 5) is closed.
 */
export const quoteSendInput = quoteIdInput;
export type QuoteSendInput = z.infer<typeof quoteSendInput>;

export const quoteApproveInput = quoteIdInput.extend({
  approvedByContactId: z.string().uuid().optional(),
  approvalChannel: z.enum(['portal', 'office_recorded']).optional(),
});
export type QuoteApproveInput = z.infer<typeof quoteApproveInput>;

export const quoteDeclineInput = quoteIdInput.extend({
  declinedReason: optionalText(500),
});
export type QuoteDeclineInput = z.infer<typeof quoteDeclineInput>;

export const quoteConvertInput = quoteIdInput.extend({
  termsDays: z.coerce.number().int().min(0).max(365).optional(),
});
export type QuoteConvertInput = z.infer<typeof quoteConvertInput>;

/**
 * Approved quote → the job that does the work, with the service type the office picks as it
 * schedules the quote (ticket 04), optionally scheduled on the spot: `toJobFields`.
 */
export const quoteToJobInput = quoteIdInput.extend(toJobFields);
export type QuoteToJobInput = z.infer<typeof quoteToJobInput>;

// ── Invoices ──────────────────────────────────────────────────────────────────
export const invoiceCreateInput = idempotency.extend({
  customerId: z.string().uuid(),
  propertyId: z.string().uuid().optional(),
  jobId: z.string().uuid().optional(),
  memo: optionalText(2000),
  /** Optional, NOT defaulted to 0. The server has to be able to tell "the user typed
   *  0%" from "the caller said nothing" — the first is a decision (resale, out of
   *  state), the second means fall back to the shop's rate. `.default(0)` collapsed
   *  both into 0 and is why every invoice in the product went out untaxed. */
  taxRateBps: z.coerce.number().int().min(0).max(20_000).optional(),
  poNumber: optionalText(60),
  termsDays: z.coerce.number().int().min(0).max(365).optional(),
  lines: z.array(lineItemInput).min(1).max(200),
});
export type InvoiceCreateInput = z.infer<typeof invoiceCreateInput>;

/**
 * Bill a job. Lines are optional: a job that came from a quote bills what was quoted, so
 * omitting them copies the quote's lines rather than making someone retype the sale.
 */
export const invoiceFromJobInput = idempotency.extend({
  jobId: z.string().uuid(),
  termsDays: z.coerce.number().int().min(0).max(365).optional(),
  poNumber: optionalText(60),
  taxRateBps: z.coerce.number().int().min(0).max(20_000).optional(),
  lines: z.array(lineItemInput).max(200).optional(),
});
export type InvoiceFromJobInput = z.infer<typeof invoiceFromJobInput>;

/**
 * How the bill sheet prices one line for this bill only (Inventory v2 12b, decisions 5b, 6):
 * a percent off, an amount off (cents), a new unit price (cents), or No charge. Absent: the job's
 * price for the part, else the price book's.
 */
export const billAdjust = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('pct'),
    // Two decimals at most, so "Discount 12.35%" is the percent the line was worked out at.
    value: z.coerce
      .number()
      .finite()
      .gt(0)
      .max(100)
      .refine(
        (v) => Math.abs(Math.round(v * 100) - v * 100) < 1e-6,
        'Two decimals at most',
      ),
  }),
  z.object({
    kind: z.literal('amt'),
    value: z.coerce.number().int().positive().max(100_000_000),
  }),
  z.object({
    kind: z.literal('price'),
    value: z.coerce.number().int().min(0).max(100_000_000),
  }),
  z.object({ kind: z.literal('free') }),
]);
export type BillAdjust = z.infer<typeof billAdjust>;

/**
 * How the bill sheet prices hours (Inventory v2 ticket 13): any part's choice, or a set amount for
 * those hours (cents), what the customer agreed to whatever the time came to.
 */
export const timeAdjust = z.union([
  billAdjust,
  z.object({
    kind: z.literal('total'),
    value: z.coerce.number().int().positive().max(100_000_000),
  }),
]);
export type TimeAdjust = z.infer<typeof timeAdjust>;

/**
 * Bill part of a T&M job (Inventory v2 amendment P5, ticket 12a): each part used on the job
 * with how much of it to bill now, at most what is left. They go on the job's one draft, which
 * is created if there is none. Typed lines (labor, fees) stay until charges and time are billed
 * from the job (12c, 13).
 */
export const invoiceBillJobInput = idempotency
  .extend({
    jobId: z.string().uuid(),
    parts: z
      .array(
        z.object({
          jobCostId: z.string().uuid(),
          qty: z.coerce.number().finite().positive().max(100_000),
          adjust: billAdjust.optional(),
        }),
      )
      .max(200),
    /** Charges added to the job (12c, decision 10): each billed whole, priced as a part is. */
    charges: z
      .array(
        z.object({
          jobChargeId: z.string().uuid(),
          adjust: billAdjust.optional(),
        }),
      )
      .max(200)
      .default([]),
    /** Time on the job (ticket 13): hours of each stopped time entry, at most what is left. */
    time: z
      .array(
        z.object({
          timeEntryId: z.string().uuid(),
          hours: z.coerce.number().finite().positive().max(10_000),
          adjust: timeAdjust.optional(),
        }),
      )
      .max(200)
      .default([]),
    lines: z.array(lineItemInput).max(200).optional(),
    termsDays: z.coerce.number().int().min(0).max(365).optional(),
    poNumber: optionalText(60),
    taxRateBps: z.coerce.number().int().min(0).max(20_000).optional(),
  })
  .refine(
    (b) => b.parts.length + b.charges.length + b.time.length + (b.lines?.length ?? 0) > 0,
    { message: 'Pick at least one part, charge or time entry to bill' },
  )
  .refine((b) => new Set(b.parts.map((p) => p.jobCostId)).size === b.parts.length, {
    message: 'A part is listed twice',
  })
  .refine((b) => new Set(b.charges.map((c) => c.jobChargeId)).size === b.charges.length, {
    message: 'A charge is listed twice',
  })
  .refine((b) => new Set(b.time.map((t) => t.timeEntryId)).size === b.time.length, {
    message: 'A time entry is listed twice',
  });
export type InvoiceBillJobInput = z.infer<typeof invoiceBillJobInput>;

export const invoiceIssueInput = idempotency.extend({
  invoiceId: z.string().uuid(),
  termsDays: z.coerce.number().int().min(0).max(365).optional(),
  poNumber: optionalText(60),
});
export type InvoiceIssueInput = z.infer<typeof invoiceIssueInput>;

export const invoiceVoidInput = idempotency.extend({
  invoiceId: z.string().uuid(),
  reason: optionalText(500),
});
export type InvoiceVoidInput = z.infer<typeof invoiceVoidInput>;

/**
 * Email an invoice to its customer. No address input (MAP 249 spec §3): the recipient is always
 * the customer's address on file. An `email` a caller still sends is STRIPPED here (zod's
 * default), so it can never reach the outbox: the open relay (R1 gap 5) is closed.
 */
export const invoiceSendInput = idempotency.extend({
  invoiceId: z.string().uuid(),
});
export type InvoiceSendInput = z.infer<typeof invoiceSendInput>;

export const formatInvoiceNumber = (n: number): string => `INV-${n}`;
export const formatQuoteNumber = (n: number, revision: number): string =>
  revision > 1 ? `Q-${n}r${revision}` : `Q-${n}`;

/**
 * `open` narrows to invoices with money still on them (issued / partially paid). The Today
 * screen's "needs your attention" queue used to filter a downloaded array for overdue
 * invoices, which silently stopped working the moment the array became one page — the
 * oldest overdue invoice is exactly the one that falls off the end.
 */
export const invoiceListInput = listInput.extend({
  open: z.boolean().optional(),
  /**
   * `overdue` finishes the job `open` started (117). `open` narrowed the page to invoices
   * with money on them, but /today still filtered THAT page for the overdue ones in the
   * browser — so the list was "the overdue invoices among the first 50 open ones", and its
   * count was that subset's length. Both are wrong for a shop with more than 50 open
   * invoices, and wrong in the direction that hides work.
   *
   * ⚠️ It is a DATE question, so the server answers it on the ORG's clock — the same
   * `orgToday` comparison `invoiceSummary.overdueCount` uses, which is what makes the KPI
   * and this list agree by construction rather than by luck.
   */
  overdue: z.boolean().optional(),
});
export type InvoiceListInput = z.infer<typeof invoiceListInput>;

/** Back to job (12b, P10): a part line comes off the job's draft, left to bill again. */
export const invoiceLineBackToJobInput = idempotency.extend({
  lineId: z.string().uuid(),
});
export type InvoiceLineBackToJobInput = z.infer<typeof invoiceLineBackToJobInput>;

/** The price a T&M job bills a used part at (12b, decision 5); null for the price book's. */
export const partsBillingSetJobPriceInput = idempotency.extend({
  jobCostId: z.string().uuid(),
  priceCents: z.coerce.number().int().min(0).max(100_000_000).nullable(),
});
export type PartsBillingSetJobPriceInput = z.infer<typeof partsBillingSetJobPriceInput>;

/**
 * Write off some of a part used on a T&M job (12c, amendment P8 decision 3): never invoiced, with
 * a typed reason; its cost stays on the job.
 */
export const partsBillingWriteOffInput = idempotency.extend({
  jobCostId: z.string().uuid(),
  qty: z.coerce.number().finite().positive().max(100_000),
  reason: z.string().trim().min(1, 'Say why it won’t be billed').max(500),
});
export type PartsBillingWriteOffInput = z.infer<typeof partsBillingWriteOffInput>;

/** Undo a write-off (12c): what it took is left to bill again. */
export const partsBillingUndoWriteOffInput = idempotency.extend({
  writeOffId: z.string().uuid(),
});
export type PartsBillingUndoWriteOffInput = z.infer<typeof partsBillingUndoWriteOffInput>;

/** Write off hours of a time entry on a T&M job (ticket 13, verdict A): never billed, with a reason. */
export const partsBillingWriteOffTimeInput = idempotency.extend({
  timeEntryId: z.string().uuid(),
  hours: z.coerce.number().finite().positive().max(10_000),
  reason: z.string().trim().min(1, 'Say why it won’t be billed').max(500),
});
export type PartsBillingWriteOffTimeInput = z.infer<typeof partsBillingWriteOffTimeInput>;

/** Undo a write-off of hours (ticket 13): they are left to bill again. */
export const partsBillingUndoTimeWriteOffInput = idempotency.extend({
  writeOffId: z.string().uuid(),
});
export type PartsBillingUndoTimeWriteOffInput = z.infer<
  typeof partsBillingUndoTimeWriteOffInput
>;

/**
 * Whether the customer's copy of a draft shows each time entry's hours and rate, or one Labor line
 * at what it bills (ticket 13, the owner 2026-10-09). Only a draft changes.
 */
export const invoiceSetShowHoursInput = idempotency.extend({
  invoiceId: z.string().uuid(),
  showHours: z.boolean(),
});
export type InvoiceSetShowHoursInput = z.infer<typeof invoiceSetShowHoursInput>;

/** A charge added to a T&M job (12c, decision 10): a trip fee, disposal, a permit. */
export const partsBillingAddChargeInput = idempotency.extend({
  jobId: z.string().uuid(),
  description: z.string().trim().min(1, 'Say what the charge is for').max(200),
  amountCents: z.coerce.number().int().positive().max(100_000_000),
  taxable: z.boolean().default(false),
});
export type PartsBillingAddChargeInput = z.infer<typeof partsBillingAddChargeInput>;

/** Remove a charge the job hasn't billed (12c). */
export const partsBillingRemoveChargeInput = idempotency.extend({
  jobChargeId: z.string().uuid(),
});
export type PartsBillingRemoveChargeInput = z.infer<typeof partsBillingRemoveChargeInput>;
